"use client";

/**
 * RunDialogs — the project-showcase modal + brief form, ported from the
 * retired Hero v2 landing (git: HeroExperience.tsx pre-#81) and restyled to
 * The Run's design system. Behavior is carried over 1:1:
 *
 *  - Project modal: URL-hash deep-links (#work-<slug>), back/forward sync via
 *    hashchange, prev/next gallery browsing (arrow keys, RTL-mirrored),
 *    focus trap + focus restore, Escape closes.
 *  - Brief form: honeypot, client validation, submitLead server action,
 *    contact_submitted analytics event, mailto fallback on the success screen.
 *  - While either dialog is open: Lenis stops + <html> overflow locks.
 *
 * The Run's shell reaches in via `handleRef` (openProject from the engine's
 * gallery raycast, openForm from the summit CTA).
 */
import { useCallback, useEffect, useImperativeHandle, useRef, useState, type Ref } from "react";
import { track } from "@vercel/analytics";
import { useLenis } from "lenis/react";
import { submitLead } from "@/app/actions/submit-lead";
import type { LeadInput } from "@/lib/schema";
import { localeHref, isLocale } from "@/lib/i18n/config";
import type { GalleryStatItem, LandingContent, ProjectDatum } from "../content";

export interface RunDialogsHandle {
  openProject: (idx: number) => void;
  /** Open the expanded dialog behind a stat card, by its no ("S1".."S4"). */
  openStat: (no: string) => void;
  openForm: () => void;
}

// Stable, locale-independent slug for a project — used for shareable
// deep-links like #work-planzero. It is the same slug the /case/<slug> route
// uses, so a modal link and a case-study link never disagree.
const projSlug = (p: ProjectDatum) => p.slug;
const pad2 = (n: number) => String(n).padStart(2, "0");

/**
 * A wall-clock reading that advances once a second while `active`, or null
 * before the first tick. The clock is the external system here — the effect
 * only subscribes to it, and the remaining time is derived during render.
 * Starting at null also keeps the server and the first client render identical.
 */
function useClock(active: boolean): number | null {
  const [now, setNow] = useState<number | null>(null);
  useEffect(() => {
    if (!active) return;
    const raf = requestAnimationFrame(() => setNow(Date.now()));
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => {
      cancelAnimationFrame(raf);
      window.clearInterval(id);
    };
  }, [active]);
  return now;
}

/** Countdown as "12d 04h 09m", dropping to seconds inside the last day. */
function formatCountdown(ms: number): string {
  const total = Math.floor(ms / 1000);
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return d > 0 ? `${d}d ${pad2(h)}h ${pad2(m)}m` : `${pad2(h)}h ${pad2(m)}m ${pad2(s)}s`;
}

export interface RunDialogsProps {
  content: LandingContent;
  projects: ProjectDatum[];
  /** The gallery's stat cards — each opens its own breakdown dialog. */
  stats: GalleryStatItem[];
  dir: "ltr" | "rtl";
  locale: string;
  handleRef: Ref<RunDialogsHandle>;
}

export function RunDialogs({ content, projects, stats, dir, locale, handleRef }: RunDialogsProps) {
  const loc = isLocale(locale) ? locale : "en";
  const privacyHref = localeHref(loc, "/privacy");
  const lenis = useLenis();

  const [project, setProject] = useState<ProjectDatum | null>(null);
  const [stat, setStat] = useState<GalleryStatItem | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formDone, setFormDone] = useState(false);
  const [formErr, setFormErr] = useState("");
  const [formStatus, setFormStatus] = useState<"idle" | "submitting">("idle");
  const [mailHref, setMailHref] = useState("#");

  const statCloseR = useRef<HTMLButtonElement>(null);
  const nameR = useRef<HTMLInputElement>(null);
  const emailR = useRef<HTMLInputElement>(null);
  const companyR = useRef<HTMLInputElement>(null);
  const typeR = useRef<HTMLSelectElement>(null);
  const ideaR = useRef<HTMLTextAreaElement>(null);
  const hpR = useRef<HTMLInputElement>(null);
  const modalCloseR = useRef<HTMLButtonElement>(null);
  const formCloseR = useRef<HTMLButtonElement>(null);
  const lastFocusRef = useRef<HTMLElement | null>(null);

  // Open a project + set a shareable URL hash. Setting location.hash pushes a
  // real history entry and fires `hashchange` on back/forward — the hashchange
  // listener below is the single source that syncs modal state to the URL.
  const openProject = useCallback((p: ProjectDatum | null) => {
    if (!p) return;
    setStat(null); // the two showcase dialogs are mutually exclusive
    setProject(p); // immediate; the hashchange listener keeps it in sync
    const h = `work-${projSlug(p)}`;
    if (window.location.hash !== `#${h}`) window.location.hash = h;
  }, []);

  // Same contract for a stat card: deep-linkable as #stat-s1..s4.
  const openStat = useCallback((no: string) => {
    const s = stats.find((x) => x.no.toLowerCase() === no.toLowerCase());
    if (!s) return;
    setProject(null);
    setStat(s);
    const h = `stat-${s.no.toLowerCase()}`;
    if (window.location.hash !== `#${h}`) window.location.hash = h;
  }, [stats]);

  // Close from the UI: drop the modal + strip the dialog hash from the
  // current entry (back button is handled separately by hashchange).
  const closeProject = useCallback(() => {
    setProject(null);
    if (window.location.hash.startsWith("#work-")) {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }
  }, []);

  const closeStat = useCallback(() => {
    setStat(null);
    if (window.location.hash.startsWith("#stat-")) {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }
  }, []);

  // Step to the previous/next project without leaving the modal. Reuses the
  // hash so each step is a shareable deep-link + a back-button stop. Clamps.
  const stepProject = useCallback((delta: number) => {
    setProject((cur) => {
      if (!cur) return cur;
      const i = projects.findIndex((x) => x.name === cur.name);
      const next = projects[i + delta];
      if (!next) return cur;
      const h = `work-${projSlug(next)}`;
      if (window.location.hash !== `#${h}`) window.location.hash = h;
      return next;
    });
  }, [projects]);

  const openForm = useCallback(() => {
    setFormErr("");
    setFormDone(false);
    setFormOpen(true);
  }, []);

  useImperativeHandle(handleRef, () => ({
    openProject: (idx: number) => openProject(projects[idx] ?? null),
    openStat,
    openForm,
  }), [openProject, openStat, openForm, projects]);

  // Escape closes whichever dialog is open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeProject();
        closeStat();
        setFormOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeProject, closeStat]);

  // Move focus into the open dialog; restore it to the ORIGINAL trigger only
  // once every dialog has closed. One combined effect on purpose: with one
  // effect per dialog, a stat→project hop had the project effect overwrite
  // lastFocusRef (with the body — the stat overlay just went inert) and the
  // stat effect then "restore" focus right back out of the new modal.
  useEffect(() => {
    if (project || stat || formOpen) {
      if (!lastFocusRef.current) {
        lastFocusRef.current = document.activeElement as HTMLElement;
      }
      (project ? modalCloseR : stat ? statCloseR : formCloseR).current?.focus();
    } else if (lastFocusRef.current) {
      lastFocusRef.current.focus?.();
      lastFocusRef.current = null;
    }
  }, [project, stat, formOpen]);

  // Lock page scroll while a dialog is open — stop the global Lenis (desktop)
  // and lock native <html> overflow (mobile / fallback).
  useEffect(() => {
    const open = !!project || !!stat || formOpen;
    if (open) {
      lenis?.stop();
      document.documentElement.style.overflow = "hidden";
    } else {
      lenis?.start();
      document.documentElement.style.overflow = "";
    }
    return () => {
      document.documentElement.style.overflow = "";
      lenis?.start();
    };
  }, [project, stat, formOpen, lenis]);

  // The URL hash is the source of truth for both showcase dialogs —
  // back/forward and shared links all flow through here.
  useEffect(() => {
    const onHash = () => {
      const wm = window.location.hash.match(/^#work-(.+)$/);
      const sm = window.location.hash.match(/^#stat-(.+)$/);
      setProject(wm ? projects.find((x) => projSlug(x) === wm[1]) ?? null : null);
      setStat(sm ? stats.find((s) => s.no.toLowerCase() === sm[1].toLowerCase()) ?? null : null);
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, [projects, stats]);

  // Deep-link on load: #work-<slug> / #stat-<no> opens that dialog.
  useEffect(() => {
    const pm = window.location.hash.match(/^#work-(.+)$/);
    const sm = window.location.hash.match(/^#stat-(.+)$/);
    const p = pm ? projects.find((x) => projSlug(x) === pm[1]) : null;
    const s = sm ? stats.find((x) => x.no.toLowerCase() === sm[1].toLowerCase()) : null;
    if (!p && !s) return;
    const t = setTimeout(() => {
      if (p) setProject(p);
      else if (s) setStat(s);
    }, 0);
    return () => clearTimeout(t);
  }, [projects, stats]);

  // Trap Tab focus within an open dialog (WCAG 2.4.3).
  const trapFocus = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab") return;
    const nodes = e.currentTarget.querySelectorAll<HTMLElement>(
      'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])',
    );
    const list = Array.from(nodes).filter((el) => el.offsetParent !== null || el === document.activeElement);
    if (!list.length) return;
    const first = list[0];
    const last = list[list.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  // Arrow keys browse projects while the modal is open (RTL-mirrored), then
  // fall through to the focus trap.
  const projModalKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      const forward = (e.key === "ArrowRight") !== (dir === "rtl");
      e.preventDefault();
      stepProject(forward ? 1 : -1);
      return;
    }
    trapFocus(e);
  }, [dir, stepProject, trapFocus]);

  const submitBrief = useCallback(async () => {
    if (formStatus === "submitting") return;
    const name = nameR.current?.value.trim() ?? "";
    const email = emailR.current?.value.trim() ?? "";
    const company = companyR.current?.value.trim() ?? "";
    const projectType = (typeR.current?.value ?? "web-app") as LeadInput["projectType"];
    const idea = ideaR.current?.value.trim() ?? "";
    if (!name) { setFormErr(content.form.errName); nameR.current?.focus(); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { setFormErr(content.form.errEmail); emailR.current?.focus(); return; }
    if (idea.length < 20) { setFormErr(content.form.errIdea); ideaR.current?.focus(); return; }
    setFormErr("");
    setFormStatus("submitting");
    const result = await submitLead({ name, email, company, projectType, idea }, hpR.current?.value ?? "");
    setFormStatus("idle");
    if (!result.ok) { setFormErr(result.error); return; }
    track("contact_submitted", { source: "brief_form", projectType });
    const label = content.form.types.find((tp) => tp.value === projectType)?.label ?? projectType;
    const subject = `New brief — ${label} — ${name}`;
    const body = `Name: ${name}\nEmail: ${email}\nCompany: ${company || "—"}\nProject type: ${label}\n\nIdea:\n${idea}`;
    setMailHref(`mailto:${content.contact.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
    setFormDone(true);
  }, [content, formStatus]);

  // gallery position for the modal's prev/next browser (clamped at the ends).
  const projIndex = project ? projects.findIndex((x) => x.name === project.name) : -1;
  const prevProj = projIndex > 0 ? projects[projIndex - 1] : null;
  const nextProj = projIndex >= 0 && projIndex < projects.length - 1 ? projects[projIndex + 1] : null;

  // Pre-launch projects count down to their announced launch moment; for every
  // other project the clock stays idle and msToLaunch is null.
  const now = useClock(!!project?.launchAt);
  const launchTs = project?.launchAt ? new Date(project.launchAt).getTime() : NaN;
  const msToLaunch =
    now !== null && !Number.isNaN(launchTs) ? launchTs - now : null;
  const launchDate = Number.isNaN(launchTs)
    ? null
    : new Intl.DateTimeFormat(locale, { day: "2-digit", month: "short", year: "numeric" })
        .format(launchTs);

  return (
    <>
      {/* project showcase modal */}
      <div
        onClick={closeProject}
        role="dialog"
        aria-modal={project ? true : undefined}
        aria-label={project?.name}
        inert={!project}
        className={`run-dlg-overlay${project ? " is-open" : ""}`}
      >
        {/* data-lenis-prevent: Lenis owns the wheel document-wide on desktop, so
            without this the dialog's own overflow never sees a wheel event and
            taller-than-viewport content is unreachable. */}
        <div onClick={(e) => e.stopPropagation()} onKeyDown={projModalKeyDown} className="run-dlg" data-lenis-prevent>
          <button ref={modalCloseR} onClick={closeProject} aria-label="Close" className="run-dlg-close">✕</button>
          <div
            className="run-dlg-hero"
            // An app without a capture yet (empty thumb) gets a status-tinted
            // wash rather than a broken url("") request.
            style={
              project
                ? project.thumb
                  ? { backgroundImage: `url(${project.thumb})` }
                  : { backgroundImage: `radial-gradient(ellipse at 30% 20%, ${project.statusColor}33 0%, transparent 65%), linear-gradient(#14273f, #0b1930)` }
                : undefined
            }
          >
            <div className="run-dlg-hero-scrim" aria-hidden />
            {/* <bdi dir="ltr">: an "01 / 09" ordinal would otherwise reorder to
                "09 / 01" under the Arabic layout's RTL base direction. bdi
                isolates the run without changing the element's own alignment. */}
            <span className="run-dlg-hero-no" aria-hidden>
              <bdi dir="ltr">{project ? `${project.no} / ${pad2(projects.length)}` : ""}</bdi>
            </span>
            {project && (
              <span
                className="run-status"
                style={{ ["--status" as string]: project.statusColor }}
              >
                <span
                  className={`run-status-dot${project.status === "LIVE" ? " is-live" : ""}`}
                  aria-hidden
                />
                {project.statusLabel}
              </span>
            )}
          </div>
          <div className="run-dlg-body">
            <p className="run-dlg-kind">{project?.kind ?? "—"}</p>
            <h3 className="run-dlg-name">{project?.name ?? "—"}</h3>
            <p className="run-dlg-blurb">{project?.blurb ?? "—"}</p>

            {/* Pre-launch band — only a project with a real announced date gets
                one, so nothing here can imply a launch that was never set. */}
            {project?.launchAt && (
              <div className="run-dlg-launch">
                <span className="run-dlg-launch-label">
                  {msToLaunch !== null && msToLaunch <= 0
                    ? content.modal.launching
                    : content.modal.launchesIn}
                </span>
                <span className="run-dlg-launch-value tabular-nums">
                  {msToLaunch === null
                    ? launchDate
                    : msToLaunch > 0
                      ? formatCountdown(msToLaunch)
                      : launchDate}
                </span>
                {msToLaunch !== null && msToLaunch > 0 && launchDate && (
                  <span className="run-dlg-launch-date">· {launchDate}</span>
                )}
              </div>
            )}

            <dl className="run-dlg-specs">
              <div className="run-dlg-spec">
                <dt>{content.modal.specTimeline}</dt>
                {/* bdi keeps "2025 → 2026" from flipping to "2026 → 2025" in RTL */}
                <dd className="tabular-nums"><bdi dir="ltr">{project?.year ?? "—"}</bdi></dd>
              </div>
              <div className="run-dlg-spec">
                <dt>{content.modal.specStatus}</dt>
                <dd style={{ color: project?.statusColor }}>{project?.statusLabel ?? "—"}</dd>
              </div>
              <div className="run-dlg-spec">
                <dt>{content.modal.specLanguages}</dt>
                <dd>{project?.locale ?? "—"}</dd>
              </div>
            </dl>

            <p className="run-dlg-stack-label">{content.modal.stackLabel}</p>
            <div className="run-dlg-stack">
              {project?.stack.map((s) => (
                <span key={s} className="run-chip">{s}</span>
              ))}
            </div>

            <div className="run-dlg-actions">
              {project && project.url && project.url !== "#" && (
                <a href={project.url} target="_blank" rel="noopener noreferrer" className="run-cta run-dlg-visit">
                  {content.modal.visit} →
                </a>
              )}
              {project && (
                <a
                  href={localeHref(loc, `/case/${project.slug}`)}
                  className="run-cta run-cta--ghost run-dlg-case"
                >
                  {content.modal.caseStudy} →
                </a>
              )}
            </div>
            {/* in-modal gallery browser — step through projects without closing */}
            <div className="run-dlg-nav">
              <button type="button" className="run-projnav" onClick={() => stepProject(-1)} disabled={!prevProj} aria-label={prevProj ? `${content.modal.prev}: ${prevProj.name}` : content.modal.prev}>
                <span aria-hidden>{dir === "rtl" ? "→" : "←"}</span>
                <span className="run-projnav-label">{prevProj?.name ?? ""}</span>
              </button>
              <span aria-hidden className="run-dlg-count">
                {projIndex >= 0 ? `${pad2(projIndex + 1)} / ${pad2(projects.length)}` : ""}
              </span>
              <button type="button" className="run-projnav run-projnav--next" onClick={() => stepProject(1)} disabled={!nextProj} aria-label={nextProj ? `${content.modal.next}: ${nextProj.name}` : content.modal.next}>
                <span className="run-projnav-label">{nextProj?.name ?? ""}</span>
                <span aria-hidden>{dir === "rtl" ? "←" : "→"}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* stat index dialog — the expanded data behind each stat card. Same
          overlay/scroll-lock/focus rules as the project modal; rows that name
          an app hop straight into that app's showcase. */}
      <div
        onClick={closeStat}
        role="dialog"
        aria-modal={stat ? true : undefined}
        aria-label={stat?.label}
        inert={!stat}
        className={`run-dlg-overlay${stat ? " is-open" : ""}`}
      >
        <div onClick={(e) => e.stopPropagation()} onKeyDown={trapFocus} className="run-dlg run-dlg--stat" data-lenis-prevent>
          <button ref={statCloseR} onClick={closeStat} aria-label="Close" className="run-dlg-close">✕</button>
          <div className="run-dlg-stat-head">
            {/* bdi: "149" must not reorder inside the RTL layout */}
            <span className="run-dlg-stat-value tabular-nums" aria-hidden>
              <bdi dir="ltr">{stat?.value ?? ""}</bdi>
            </span>
            <div>
              <p className="run-dlg-kind">{stat ? `${stat.no} — ${content.modal.index}` : "—"}</p>
              <h3 className="run-dlg-name">{stat?.label ?? "—"}</h3>
            </div>
          </div>
          <p className="run-dlg-blurb">{stat?.body ?? ""}</p>
          <ul className="run-dlg-stat-rows">
            {stat?.rows.map((r) => (
              <li key={r.k}>
                {r.slug ? (
                  <button
                    type="button"
                    className="run-statrow run-statrow--link"
                    onClick={() => {
                      const p = projects.find((x) => x.slug === r.slug);
                      if (p) openProject(p); // openProject clears the stat dialog
                    }}
                  >
                    <span className="run-statrow-k">{r.k}</span>
                    {r.v && <span className="run-statrow-v">{r.v}</span>}
                    <span aria-hidden className="run-statrow-arrow">{dir === "rtl" ? "←" : "→"}</span>
                  </button>
                ) : (
                  <div className="run-statrow">
                    <span className="run-statrow-k">{r.k}</span>
                    {r.v && <span className="run-statrow-v">{r.v}</span>}
                  </div>
                )}
              </li>
            ))}
          </ul>
          {stat?.cta && (
            <div className="run-dlg-actions">
              <a href={stat.cta.url} target="_blank" rel="noopener noreferrer" className="run-cta run-dlg-visit">
                {content.modal.visit} →
              </a>
              <a href={localeHref(loc, `/case/${stat.cta.caseSlug}`)} className="run-cta run-cta--ghost">
                {content.modal.caseStudy} →
              </a>
            </div>
          )}
        </div>
      </div>

      {/* brief form */}
      <div
        onClick={() => setFormOpen(false)}
        role="dialog"
        aria-modal={formOpen ? true : undefined}
        aria-label={content.form.eyebrow}
        inert={!formOpen}
        className={`run-dlg-overlay run-dlg-overlay--form${formOpen ? " is-open" : ""}`}
      >
        <div onClick={(e) => e.stopPropagation()} onKeyDown={trapFocus} className="run-dlg run-dlg--form" data-lenis-prevent>
          <button ref={formCloseR} onClick={() => setFormOpen(false)} aria-label="Close" className="run-dlg-close">✕</button>

          {!formDone ? (
            <div>
              {/* honeypot — off-screen; bots that auto-fill every field trip it */}
              <input ref={hpR} type="text" name="company_url" tabIndex={-1} autoComplete="off" aria-hidden className="run-hp" />
              <p className="run-eyebrow">{content.form.eyebrow}</p>
              <h3 className="run-dlg-name run-dlg-name--form">{content.form.title}</h3>
              <div className="run-formgrid">
                <label className="run-field">
                  <span className="run-label">{content.form.name}</span>
                  <input ref={nameR} type="text" placeholder={content.form.namePh} aria-describedby="run-form-err" className="run-input" />
                </label>
                <label className="run-field">
                  <span className="run-label">{content.form.email}</span>
                  <input ref={emailR} type="email" placeholder={content.form.emailPh} aria-describedby="run-form-err" className="run-input" />
                </label>
                <label className="run-field">
                  <span className="run-label">{content.form.company} <span className="run-label-opt">{content.form.companyOpt}</span></span>
                  <input ref={companyR} type="text" placeholder={content.form.companyPh} className="run-input" />
                </label>
                <label className="run-field">
                  <span className="run-label">{content.form.type}</span>
                  <select ref={typeR} className="run-input run-select">
                    {content.form.types.map((tp) => (
                      <option key={tp.value} value={tp.value}>{tp.label}</option>
                    ))}
                  </select>
                </label>
              </div>
              <label className="run-field run-field--idea">
                <span className="run-label">{content.form.idea}</span>
                <textarea ref={ideaR} rows={4} placeholder={content.form.ideaPh} aria-describedby="run-form-err" className="run-input run-textarea" />
              </label>
              <div id="run-form-err" role="alert" aria-live="assertive" className={`run-form-err${formErr ? " is-on" : ""}`}>{formErr}</div>
              <button onClick={submitBrief} disabled={formStatus === "submitting"} className="run-cta run-send">
                {formStatus === "submitting" ? content.form.submitting : `${content.form.submit} →`}
              </button>
              <p className="run-consent">
                {content.form.consent}{" "}
                <a href={privacyHref}>{content.form.privacyPolicy}</a>.
              </p>
            </div>
          ) : (
            <div className="run-form-done">
              <div className="run-form-done-mark" aria-hidden>✓</div>
              <h3 className="run-dlg-name run-dlg-name--form">{content.form.doneTitle}</h3>
              <p className="run-dlg-blurb run-form-done-body">{content.form.doneBody}</p>
              <a href={mailHref} className="run-cta">{content.form.doneCta} →</a>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
